"use client";

import { Check } from "lucide-react";
import Link from "@/components/ui/link";
import { cn } from "@/lib/utils";
import { buildPushNudgeConfirmation } from "../lib/push-nudge-copy";

export type PushNudgeConfirmationProps = {
  /** What this user will actually receive, in their own words. */
  scopes: string[];
  className?: string;
};

/**
 * V6 — the post-grant confirmation.
 *
 * The grant is not the win; keeping it is. A user who says yes and whose first
 * notification is irrelevant mutes the app at the OS level, and on iOS that is
 * unrecoverable — worse than never having asked. So the moment after a grant is
 * spent telling them exactly what they signed up for and where to narrow it.
 *
 * `scopes` is passed in from the caller's live preference set rather than
 * hardcoded here, so this line cannot drift away from the preferences that
 * actually gate delivery.
 */
export function PushNudgeConfirmation({
  scopes,
  className,
}: PushNudgeConfirmationProps) {
  const { headline, subhead } = buildPushNudgeConfirmation(scopes);

  return (
    <section
      aria-live="polite"
      className={cn(
        "flex items-start gap-3 rounded-2xl border border-brand/20 bg-brand/[0.06] px-4 py-3",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground"
      >
        <Check className="h-4 w-4" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-[15px] leading-tight font-semibold text-text-primary">
          {headline}
        </p>
        <p className="mt-0.5 text-[13px] leading-snug text-text-secondary">
          {subhead}
        </p>
        <Link
          href="/settings/notifications"
          className="mt-1.5 inline-block text-[13px] font-medium text-brand underline underline-offset-2"
        >
          Choose what reaches you
        </Link>
      </div>
    </section>
  );
}
